import { Button, type ButtonProps } from '../primitives/button.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Share } from '@tunasong/icons'
import { entitySort } from '@tunasong/models'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import { EntityLink } from '../entity/entity-link.js'
import { Menu, MenuItem } from '../primitives/index.js'

export interface BacklinksButtonProps extends ButtonProps {
  id?: string
}

export const BacklinksButton: FC<BacklinksButtonProps> = ({ id = '', ...restProps }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])
  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])
  const { backlinks = [] } = graphHooks.useEntityBacklinks({ entityId: id })

  const ids = backlinks
    .map(backlink => backlink.source)
    .filter(id => id !== 'ROOT')
    .filter(Boolean)
  const { entities: linkedEntities = [] } = graphHooks.useEntitiesById(ids)
  const entities = entitySort({ entities: linkedEntities })

  const numBacklinks = backlinks.length

  const label = numBacklinks > 0 ? `${numBacklinks}` : ''

  return (
    <>
      <Button
        {...restProps}
        endIcon={<Share />}
        aria-label={`Show ${numBacklinks} backlinks`}
        aria-controls={open ? 'backlink-button' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={backlinks.length === 0}
        onClick={handleClick}
      >
        {label}
      </Button>

      <Menu
        id="backlink-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'backlink-button',
        }}
      >
        <MenuItem disabled>Backlinks</MenuItem>
        {entities.map(entity =>
          entity ? (
            <MenuItem onClick={handleClose} key={entity.id}>
              <EntityLink entity={entity} onClick={handleClose} />
            </MenuItem>
          ) : null
        )}
      </Menu>
    </>
  )
}

export default BacklinksButton
